import { useState, useRef, useCallback } from "react";
import { CloudUpload, ArrowUpFromLine, FileUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { DemoFilePicker } from "./DemoFilePicker";
import { CONTENT_LIMITS } from "@/packages/domains/documents";
import { toast } from "sonner";
import { useTranslation } from "@/packages/utils";
import { useWorkspace } from "@/routes/$workspaceSlug/route";

const ACCEPTED_TYPES = ".pdf,.xlsx,.txt";

function filterOversizedFiles(
  files: File[],
  errorMessage: (name: string, size: string) => string,
): File[] {
  const valid: File[] = [];
  for (const file of files) {
    if (file.size > CONTENT_LIMITS.MAX_FILE_SIZE_FOR_EXTRACTION) {
      const sizeMB = (file.size / 1_000_000).toFixed(1);
      toast.error(errorMessage(file.name, sizeMB));
    } else {
      valid.push(file);
    }
  }
  return valid;
}

interface UploadDropZoneProps {
  collapsed: boolean;
  onFilesSelected: (files: File[]) => void;
  disabled: boolean;
}

export function UploadDropZone({
  collapsed,
  onFilesSelected,
  disabled,
}: UploadDropZoneProps) {
  const { t } = useTranslation("documents");
  const {
    workspace: { isDemo },
  } = useWorkspace();
  const [isDragOver, setIsDragOver] = useState(false);
  const [demoPickerOpen, setDemoPickerOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const fileSizeError = useCallback(
    (name: string, size: string) =>
      t("dropZone.fileSizeExceeded", { name, size }),
    [t],
  );

  const handleBrowse = () => {
    if (isDemo) {
      setDemoPickerOpen(true);
    } else {
      inputRef.current?.click();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      const valid = filterOversizedFiles(Array.from(fileList), fileSizeError);
      if (valid.length > 0) {
        onFilesSelected(valid);
      }
    }
    e.target.value = "";
  };

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled && !isDemo) setIsDragOver(true);
    },
    [disabled, isDemo],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (disabled || isDemo) return;

      const droppedFiles = filterOversizedFiles(
        Array.from(e.dataTransfer.files),
        fileSizeError,
      );
      if (droppedFiles.length > 0) {
        onFilesSelected(droppedFiles);
      }
    },
    [disabled, isDemo, onFilesSelected, fileSizeError],
  );

  const hiddenInput = !isDemo && (
    <input
      ref={inputRef}
      type="file"
      multiple
      accept={ACCEPTED_TYPES}
      onChange={handleInputChange}
      className="hidden"
    />
  );

  /* ── Collapsed: minimal inline link ── */
  if (collapsed) {
    return (
      <div>
        {hiddenInput}
        {isDemo && (
          <DemoFilePicker
            open={demoPickerOpen}
            onClose={() => setDemoPickerOpen(false)}
            onFilesSelected={(files) => {
              setDemoPickerOpen(false);
              onFilesSelected(files);
            }}
          />
        )}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: drop zone needs drag event handlers */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <button
            type="button"
            onClick={handleBrowse}
            disabled={disabled}
            className={`w-full flex items-center justify-center gap-2 rounded-lg border border-dashed py-2.5 text-[12px] font-medium transition-all duration-200 cursor-pointer ${
              disabled
                ? "border-border/30 text-muted/40 cursor-not-allowed"
                : isDragOver
                  ? "border-accent text-accent bg-accent/5"
                  : "border-border/80 text-muted/80 hover:border-accent/50 hover:text-accent hover:bg-accent/[0.02]"
            }`}
          >
            <FileUp size={13} strokeWidth={2} />
            {disabled
              ? t("dropZone.fileLimitReached")
              : t("dropZone.dropOrBrowse")}
          </button>
        </div>
      </div>
    );
  }

  /* ── Expanded: full-bleed drop zone ── */
  return (
    <>
      {isDemo && (
        <DemoFilePicker
          open={demoPickerOpen}
          onClose={() => setDemoPickerOpen(false)}
          onFilesSelected={(files) => {
            setDemoPickerOpen(false);
            onFilesSelected(files);
          }}
        />
      )}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drop zone needs drag event handlers */}
      <div
        onDragOver={isDemo ? undefined : handleDragOver}
        onDragLeave={isDemo ? undefined : handleDragLeave}
        onDrop={isDemo ? undefined : handleDrop}
        className={`relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed transition-all duration-300 overflow-hidden ${
          isDragOver && !isDemo
            ? "border-accent bg-accent/[0.04]"
            : "border-border/60 bg-page/30 hover:border-border hover:bg-page/50"
        }`}
        style={{ minHeight: "calc(100% - 16px)" }}
      >
        {hiddenInput}

        <AnimatePresence mode="wait">
          {isDragOver && !isDemo ? (
            <motion.div
              key="drop"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="flex flex-col items-center"
            >
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{
                  duration: 1.4,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "easeInOut",
                }}
              >
                <ArrowUpFromLine
                  size={28}
                  className="text-accent mb-4"
                  strokeWidth={1.5}
                />
              </motion.div>
              <p className="text-[14px] font-semibold text-accent">
                {t("dropZone.dropToUpload")}
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="browse"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col items-center p-4"
            >
              <div className="relative mb-6">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent/[0.07]">
                  <CloudUpload
                    size={28}
                    className={`text-accent/70 transition-transform duration-300 ${
                      !isDemo ? "group-hover:scale-105" : ""
                    }`}
                    strokeWidth={1.5}
                  />
                </div>
              </div>

              <p className="text-[15px] font-semibold text-primary mb-1.5 tracking-[-0.01em]">
                {t("dropZone.dropFilesHere")}
              </p>
              <p className="text-[12px] text-muted/60 mb-6">
                {t("dropZone.fileTypes")}
              </p>

              <button
                type="button"
                onClick={handleBrowse}
                className="px-5 py-2 rounded-lg bg-accent text-white text-[13px] font-medium hover:bg-accent/90 transition-colors cursor-pointer shadow-sm"
              >
                {t("dropZone.browseFiles")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
